import { useState } from "react";
import type { ElmaCard, ElmaCheck, ElmaConfig, ElmaEntry } from "../../shared/api/kdElmaTypes";
import type { KDRun, KDLocation } from "../../shared/api/kdTypes";
import { Badge, Button, Callout } from "../../shared/ui/Primitives";

const fieldNames: Record<ElmaCheck["field"], string> = {
  designation: "Обозначение", name: "Наименование", document_type: "Вид документа",
  status: "Статус", belonging: "Принадлежность",
};
const outcomeNames: Record<ElmaEntry["outcome"], string> = {
  matched: "Обозначение найдено", not_found: "Точного обозначения нет в реестре",
  candidates: "Найдены только кандидаты", ambiguous: "Несколько точных карточек",
  needs_review: "Требуется уточнение",
};
const date = (value: string) => new Date(value).toLocaleString("ru-RU");

function Card({ card }: { card: ElmaCard }) {
  return <article className="kd-elma-card">
    <strong>Карточка ELMA #{card.document_id} · {card.designation}</strong>
    <p>{card.document_name || "Наименование не указано"}</p>
    <p>{card.drawing_type || "Вид документа не указан"} · Статус: {card.status_name || "не указан"} · Версия: {card.version_number ?? "не указана"}</p>
    {card.is_deleted === true && <Badge tone="danger">Удалена</Badge>}
    {card.is_archived === true && <Badge tone="warning">Архивная</Badge>}
    {card.candidate_reason && <Callout tone="warning">Кандидат для специалиста. {card.candidate_reason}. Обозначение PDF сохранено без исправлений.</Callout>}
    {card.checks.length > 0 && <ul className="kd-elma-checks">{card.checks.map(check => <li key={check.field}>
      <Badge tone={check.status === "mismatch" ? "danger" : check.status === "match" ? "success" : "warning"}>{fieldNames[check.field]}: {({ match: "совпадает", mismatch: "расхождение", unknown: "недостаточно данных", needs_review: "требуется сверка" })[check.status]}</Badge>
      <p>{check.reason}</p>
    </li>)}</ul>}
    <p><strong>Продукция / направления:</strong></p>
    {card.product_groups.length ? <ul>{card.product_groups.map((g, i) => <li key={i}>{g.product_name || "Продукция не указана"} · {g.product_group_name || "Группа не указана"}</li>)}</ul> : <p className="muted">Сведения о принадлежности отсутствуют.</p>}
    {card.extra_belongings.length > 0 && <><p><strong>Дополнительные принадлежности:</strong></p><ul>{card.extra_belongings.map((e, i) => <li key={i}>{e.extra_belonging_name || "Наименование не указано"}</li>)}</ul></>}
    <details><summary>Реквизиты карточки и источника</summary><pre className="kd-normative-excerpt">{JSON.stringify(card.raw, null, 2)}</pre></details>
  </article>;
}

export function ElmaEvidence({ entry }: { entry: ElmaEntry }) {
  return <div className="kd-elma-evidence">
    <p>{entry.designation_check.reason}</p>
    {entry.selection_reason && <p>{entry.selection_reason}</p>}
    {entry.cards.map(card => <Card key={card.document_id} card={card} />)}
    {!!entry.alternative_cards?.length && <details><summary>Другие варианты обозначения ({entry.alternative_cards.length})</summary>
      {entry.alternative_cards.map(card => <Card key={card.document_id} card={card} />)}
    </details>}
    {!!entry.history_cards?.length && <details><summary>История: отменённые, удалённые и архивные карточки ({entry.history_cards.length})</summary>
      {entry.history_cards.map(card => <Card key={card.document_id} card={card} />)}
    </details>}
    {entry.candidates.length > 0 && <><h4>Кандидаты — выбор специалиста</h4>{entry.candidates.map(card => <Card key={card.document_id} card={card} />)}</>}
  </div>;
}

export function KDElmaPanel({ config, run, onSelect }: {
  config: ElmaConfig; run: KDRun; onSelect: (location: KDLocation, findingId: string) => void;
}) {
  const [offset, setOffset] = useState(0);
  const report = config.report;
  const waiting = config.status === "ready" && (run.status === "queued" || run.status === "running");
  const entries = report?.entries ?? [];
  const contextLabel = Object.values(config.context_labels).join(" · ") ||
    Object.entries(config.context).filter(([, value]) => value != null).map(([field, value]) =>
      `${({ product_id: "Продукция", product_group_id: "Направление", extra_belonging_id: "Изделие" } as Record<string, string>)[field]} #${value}`
    ).join(" · ") || "не задан; принадлежности показаны для справки";
  return <details className="kd-coverage kd-elma" open>
    <summary>Сверка с ELMA · {report ? ({ checked: "выполнена", partial: "частичная", not_checked: "не выполнена" })[report.status] : waiting ? "ожидает обработки" : "не выполнялась"}</summary>
    <p>{report?.reason ?? config.reason}</p>
    <p><strong>Контекст запуска:</strong> {contextLabel}</p>
    <details><summary>Версии источников для этого запуска</summary>
      {config.pinned_at && <p>Зафиксированы: {date(config.pinned_at)}</p>}
      {config.snapshots.map(s => <p key={s.extract_key}><strong>{s.extract_key}</strong>: {date(s.snapshot_activated_at)} · строк: {s.total_rows}<br /><code>{s.snapshot_id}</code></p>)}
      {!config.snapshots.length && <p>Снимки источника не использовались.</p>}
    </details>
    {!!report?.limitations.length && <details><summary>Ограничения сверки ({report.limitations.length})</summary><ul>{report.limitations.map((l, i) => <li key={i}>{l}</li>)}</ul></details>}
    {entries.slice(offset, offset + 30).map((entry, i) => <details className="kd-elma-entry" key={`${offset}-${i}`}>
      <summary>{entry.designation} · {outcomeNames[entry.outcome]} · {entry.name || "наименование не прочитано"}</summary>
      {entry.expected_document_type && <p>Вид в PDF: {entry.expected_document_type}</p>}
      <div className="kd-page-buttons">{entry.locations.map((loc, n) => <Button key={n} size="small" variant="secondary" onClick={() => onSelect(loc, entry.finding_id ?? "")}>{run.manifest.find(d => d.id === loc.document_id)?.filename} · лист {loc.page}</Button>)}</div>
      <ElmaEvidence entry={entry} />
    </details>)}
    {entries.length > 30 && <div className="kd-page-buttons">
      <Button variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 30))}>Предыдущие</Button>
      <span>{offset + 1}–{Math.min(offset + 30, entries.length)} из {entries.length}</span>
      <Button variant="secondary" disabled={offset + 30 >= entries.length} onClick={() => setOffset(offset + 30)}>Следующие</Button>
    </div>}
  </details>;
}
